import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  aggregateByGranularity,
  formatYen,
  GRANULARITY_OPTIONS,
} from '../../lib/revenue-aggregation.js';
import type { Granularity } from '../../lib/revenue-aggregation.js';
import {
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
  LEGEND_PROPS,
} from '../../lib/chart-theme.js';
import { ChartFilterBar } from '../chart/ChartFilterBar.js';
import type { RevenueRecord } from '../../types/revenue.js';
import { RevenueMetricCard } from './RevenueMetricCard.js';

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計（旧Hub v0）の前受金・貸倒タブ。会費/チケット/合計の残高3カード、日次〜年次粒度切替の残高推移チャート、明細テーブルを表示する。
 * @graph-connects aggregateByGranularity [calls] 粒度別集約
 */
export function RevenueDeferredView({ rows }: { rows: RevenueRecord[] }) {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const latest = rows[rows.length - 1];
  if (!latest) return null;

  const buckets = useMemo(() => aggregateByGranularity(rows, granularity), [rows, granularity]);

  const chartData = useMemo(
    () =>
      buckets.map((b) => ({
        label: b.label,
        deferredBalanceSubscription: b.record.deferredBalanceSubscription,
        deferredBalanceTicket: b.record.deferredBalanceTicket,
        total: b.record.deferredBalanceSubscription + b.record.deferredBalanceTicket,
      })),
    [buckets],
  );

  const descBuckets = useMemo(() => [...buckets].reverse(), [buckets]);

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <RevenueMetricCard
          label={t('revenue.deferredBalanceSubscription')}
          value={formatYen(latest.deferredBalanceSubscription)}
        />
        <RevenueMetricCard
          label={t('revenue.deferredBalanceTicket')}
          value={formatYen(latest.deferredBalanceTicket)}
        />
        <RevenueMetricCard
          label={t('revenue.deferredBalanceTotal')}
          value={formatYen(latest.deferredBalanceSubscription + latest.deferredBalanceTicket)}
          sub={latest.date}
        />
      </div>

      <Card className="p-4">
        <div className="mb-3 flex items-center gap-3">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('revenue.deferredBalanceTrend')}
          </h3>
          <ChartFilterBar
            label={t('common.granularity')}
            options={GRANULARITY_OPTIONS}
            value={granularity}
            onChange={setGranularity}
          />
        </div>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
              <YAxis
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
              />
              <Tooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                formatter={((value: number, name: string) => [formatYen(value), name]) as never}
              />
              <Legend {...LEGEND_PROPS} />
              <Line
                type="monotone"
                dataKey="deferredBalanceSubscription"
                name={t('revenue.balanceSubscription')}
                stroke="#0017C1"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="deferredBalanceTicket"
                name={t('revenue.balanceTicket')}
                stroke="#3460FB"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="total"
                name={t('common.total')}
                stroke="#FE3939"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="overflow-x-auto p-0">
        <table className="min-w-full border-collapse text-right text-sm">
          <thead>
            <tr className="bg-[var(--color-bg-base)]">
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-left font-semibold text-[var(--color-text-secondary)]">
                {t('common.period')}
              </th>
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-text-secondary)]">
                {t('revenue.inflowSubscription')}
              </th>
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-text-secondary)]">
                {t('revenue.inflowTicket')}
              </th>
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-text-secondary)]">
                {t('revenue.balanceSubscription')}
              </th>
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-text-secondary)]">
                {t('revenue.balanceTicket')}
              </th>
            </tr>
          </thead>
          <tbody>
            {descBuckets.map((bucket) => (
              <tr key={bucket.label} className="hover:bg-[var(--color-bg-base)]">
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 text-left font-mono text-[var(--color-text-primary)]">
                  {bucket.label}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]">
                  {bucket.record.deferredInflowSubscription.toLocaleString('ja-JP')}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]">
                  {bucket.record.deferredInflowTicket.toLocaleString('ja-JP')}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]">
                  {bucket.record.deferredBalanceSubscription.toLocaleString('ja-JP')}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]">
                  {bucket.record.deferredBalanceTicket.toLocaleString('ja-JP')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
